import { useEffect, useRef, useState } from "react";
import { X, Download, Copy, Check } from "lucide-react";
import QRCode from "qrcode";

interface QrCodeDialogProps {
  url: string;
  displayName: string;
  onClose: () => void;
}

const QR_SIZE = 320;
const LOGO_RATIO = 0.22; // logo occupies ~22% of QR width
const LOGO_PADDING = 8; // white padding around logo for readability

export function QrCodeDialog({ url, displayName, onClose }: QrCodeDialogProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Generate QR code with logo overlay on mount
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Step 1: render QR code at H (high) error correction so logo overlay is safe
    QRCode.toCanvas(
      canvas,
      url,
      {
        width: QR_SIZE,
        margin: 2,
        errorCorrectionLevel: "H",
        color: {
          dark: "#1E1B4B",
          light: "#FFFFFF",
        },
      },
      (err) => {
        if (err) {
          console.error("QR code generation failed:", err);
          setError("二维码生成失败");
          return;
        }
        // Step 2: overlay the logo in the center
        overlayLogo(canvas).catch((overlayErr) => {
          console.warn("Logo overlay failed, showing QR without logo:", overlayErr);
          // QR code itself is still valid; we just don't show the logo
        });
      },
    );
  }, [url]);

  function handleDownload() {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `邀请二维码_${sanitizeFileName(displayName)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    }, "image/png");
  }

  async function handleCopyUrl() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent fail
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-dialog-title"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 id="qr-dialog-title" className="text-lg font-bold text-foreground">
              邀请二维码
            </h3>
            <p className="text-sm text-muted mt-1">
              <span className="font-medium text-foreground">{displayName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer text-muted hover:text-foreground transition-colors"
            aria-label="关闭"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* QR Canvas */}
        <div className="flex items-center justify-center bg-white rounded-xl p-4 border border-border">
          {error ? (
            <p className="text-sm text-error py-12">{error}</p>
          ) : (
            <canvas
              ref={canvasRef}
              className="block"
              aria-label={`${displayName} 的邀请二维码`}
            />
          )}
        </div>

        {/* URL display */}
        <div className="mt-4 rounded-lg bg-background p-3">
          <p className="text-xs text-muted mb-1">链接地址</p>
          <p className="text-xs font-mono text-foreground break-all select-all">
            {url}
          </p>
        </div>

        {/* Actions */}
        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={!!error}
            className="flex-1 cursor-pointer inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            下载二维码
          </button>
          <button
            type="button"
            onClick={handleCopyUrl}
            className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-background hover:border-primary-light"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 text-accent-dark" aria-hidden="true" />
                已复制
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" aria-hidden="true" />
                复制链接
              </>
            )}
          </button>
        </div>

        <p className="mt-4 text-xs text-muted text-center">
          扫码或保存图片分享给邀请对象
        </p>
      </div>
    </div>
  );
}

// ─── Logo Overlay Helper ────────────────────────────────────────────────────

/**
 * Loads /favicon.svg and draws it in the center of the QR canvas.
 * Adds a small white padding ring so the QR code remains scannable.
 */
async function overlayLogo(canvas: HTMLCanvasElement): Promise<void> {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Failed to load logo"));
    img.src = "/favicon.svg";
  });

  const logoSize = canvas.width * LOGO_RATIO;
  const x = (canvas.width - logoSize) / 2;
  const y = (canvas.height - logoSize) / 2;

  // White rounded rect background behind logo for readability
  const padding = LOGO_PADDING;
  const bgX = x - padding;
  const bgY = y - padding;
  const bgSize = logoSize + padding * 2;
  const radius = 8;

  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.moveTo(bgX + radius, bgY);
  ctx.lineTo(bgX + bgSize - radius, bgY);
  ctx.quadraticCurveTo(bgX + bgSize, bgY, bgX + bgSize, bgY + radius);
  ctx.lineTo(bgX + bgSize, bgY + bgSize - radius);
  ctx.quadraticCurveTo(bgX + bgSize, bgY + bgSize, bgX + bgSize - radius, bgY + bgSize);
  ctx.lineTo(bgX + radius, bgY + bgSize);
  ctx.quadraticCurveTo(bgX, bgY + bgSize, bgX, bgY + bgSize - radius);
  ctx.lineTo(bgX, bgY + radius);
  ctx.quadraticCurveTo(bgX, bgY, bgX + radius, bgY);
  ctx.closePath();
  ctx.fill();

  // Draw the logo
  ctx.drawImage(img, x, y, logoSize, logoSize);
}

function sanitizeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]/g, "_").trim() || "inviter";
}
